import { Utils } from '@/common/Utils';
import { highlightKeyword } from '@/utilities';
import { LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { SyntheticEvent } from 'react';
import { WithLocaleProps } from '../../providers/LocaleProvider';
import MoreVertIcon from '../../resources/images/icons-more-vertical.svg';
import { KLAYO_COLORS } from '../../themes';
import type { BaseTableRow, TableProps } from '../../types/components/table.types';
import { Chip } from '../Chip';
import { EnrolmentStatusText, enrolmentStatusSortValue } from '../EnrolmentStatusText';
import { ProfilePicture } from '../ProfilePicture';
import type { StudentCoursesDialogType } from './ManageTrainingTable';
import { Table } from './Table';

export type CourseStudentsDialogType = 'enrolled' | 'completed' | 'inProgress' | 'overdue';

export interface CourseStudentRow extends BaseTableRow {
  /** Grid row key only. NOT the employee id — a student who retook the course has several rows
   *  sharing one employee id, so callers that need the employee MUST read employeeId. */
  id: string;
  /** The student this row belongs to. Required: every action on a row (certificate download, send
   *  reminder, unenrol) is addressed to the employee, not to the row. */
  employeeId: string;
  name: string;
  status?: string;
  /** Past due and not passed. A MODIFIER on top of `status`, never a replacement for it — the two
   *  are rendered as separate elements (see formatEnrolmentStatus). */
  isOverdue?: boolean;
  dueDate?: string;
  courseAccessExpiresAt?: string;
  completedDate?: string;
  enrolledDate?: string;
  progress?: number;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  jobTitle?: string;
  enrolledCount?: number;
  completedCount?: number;
  inProgressCount?: number;
  notStartedCount?: number;
  failedCount?: number;
  overdueCount?: number;
  pendingCount?: number;
  learnUponEnrollmentId?: string;
  learnUponInstance?: number | null;
  /** Set on enrolments from the organisation's own LearnUpon portal; sent back on unenrol. */
  integrationId?: string | null;
  /** Internal CourseEnrollment id; drives the learning-history dialog. Null for non-local-cache rows. */
  courseEnrollmentId?: string;
  /** Internal EmployeeTraining id for manual-training rows. When set, the UI knows this is a
   *  manual training and uses the request activity feed in place of the enrolment history table. */
  employeeTrainingId?: string | null;
  /** LearningPathEnrollment id; drives the learning-history dialog in place of courseEnrollmentId
   *  when the row is a learning path. */
  learningPathEnrollmentId?: string | null;
  /** IntegrationLearnUponEnrollment id — only set on rows from the organisation's own LearnUpon
   *  portal. Used to fetch their learning history. */
  integrationEnrollmentId?: string | null;
  /** True when at least one issued certificate exists for this student + course. Drives the
   *  "View Certificate" row action. */
  hasCertificate?: boolean;
  /** Server-computed: live LearnUpon enrolment, has a courseEnrollmentId, and not yet
   *  terminal-pass. Gates the "Set due date"/"Set expiry date" row actions. */
  canSetDates?: boolean;
}

interface CourseStudentsTableProps extends TableProps<CourseStudentRow>, Partial<WithLocaleProps> {
  dialogType?: CourseStudentsDialogType;
  onStudentAction?: (event: React.MouseEvent, row: CourseStudentRow) => void;
  onCountBadgeClick?: (
    event: React.MouseEvent,
    row: CourseStudentRow,
    type: StudentCoursesDialogType
  ) => void;
}

export class CourseStudentsTable extends Table<CourseStudentRow, CourseStudentsTableProps> {
  static columns = {
    studentName: {
      field: 'name',
      label: 'manage.courseStudents.student',
    },
    status: {
      field: 'status',
      label: 'manage.courseStudents.status',
    },
    progress: {
      field: 'progress',
      label: 'manage.courseStudents.progress',
    },
    dueDate: {
      field: 'dueDate',
      label: 'manage.courseStudents.dueDate',
    },
    courseExpiry: {
      field: 'courseAccessExpiresAt',
      label: 'manage.courseStudents.courseExpiry',
    },
    completedDate: {
      field: 'completedDate',
      label: 'manage.courseStudents.completedDate',
    },
    enrolmentDate: {
      field: 'enrolmentDate',
      label: 'manage.courseStudents.enrolmentDate',
    },
    actions: {
      field: 'actions',
      label: 'tableColumns.actions',
    },
    employee: {
      field: 'employee',
      label: 'tableColumns.employee',
    },
    enrolledCount: {
      field: 'enrolledCount',
      label: 'tableColumns.enrolled',
    },
    completedCount: {
      field: 'completedCount',
      label: 'tableColumns.completed',
    },
    inProgressCount: {
      field: 'inProgressCount',
      label: 'tableColumns.inProgress',
    },
    notStartedCount: {
      field: 'notStartedCount',
      label: 'tableColumns.notStarted',
    },
    failedCount: {
      field: 'failedCount',
      label: 'tableColumns.failed',
    },
    overdueCount: {
      field: 'overdueCount',
      label: 'tableColumns.overdue',
    },
    pendingCount: {
      field: 'pendingCount',
      label: 'tableColumns.pending',
    },
    statusWithProgress: {
      field: 'statusWithProgress',
      label: 'manage.courseStudents.status',
    },
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [],
    noRowsMessage: '',
  };

  constructor(props: CourseStudentsTableProps) {
    super(props);

    this.state = {
      ...this.state,
    };
  }

  getRowId(e: CourseStudentRow): string {
    return this.tableId + '_' + e.id;
  }

  initColumns(): void {
    const { columns, sortable, onStudentAction, onCountBadgeClick, translate } = this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === CourseStudentsTable.columns.studentName) {
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.studentName.field,
            headerName: translate?.(CourseStudentsTable.columns.studentName.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1,
            width: 250,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => highlightKeyword(params.row.name || '', this.props.keyword),
          });
        }

        if (c.type === CourseStudentsTable.columns.employee)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.employee.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.employee.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1,
            minWidth: 250,
            valueGetter: (_value, row) => row.fullName,
            renderCell: (params) => (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <ProfilePicture user={params.row} size='sm' style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Tooltip title={params.row.fullName} placement='bottom'>
                    <div
                      className='klayo_employeetable_name'
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {highlightKeyword(params.row.fullName || '', this.props.keyword)}
                    </div>
                  </Tooltip>
                  <div className='klayo_employeetable_job truncate'>{params.row.jobTitle}</div>
                </div>
              </div>
            ),
          });

        if (c.type === CourseStudentsTable.columns.status)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.status.field,
            headerName: translate?.(CourseStudentsTable.columns.status.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            valueGetter: (_value, row) =>
              enrolmentStatusSortValue(row.status, row.isOverdue, translate),
            renderCell: (params) => (
              <EnrolmentStatusText
                status={params.row.status}
                isOverdue={params.row.isOverdue}
                translate={translate}
              />
            ),
          });
        if (c.type === CourseStudentsTable.columns.statusWithProgress)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.statusWithProgress.field,
            headerName: translate?.(CourseStudentsTable.columns.statusWithProgress.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 184,
            valueGetter: (_value, row) =>
              enrolmentStatusSortValue(row.status, row.isOverdue, translate),
            renderCell: (params) => (
              <Stack gap={1} sx={{ width: '100%' }}>
                <EnrolmentStatusText
                  status={params.row.status}
                  isOverdue={params.row.isOverdue}
                  translate={translate}
                />
                <LinearProgress
                  variant='determinate'
                  value={params.row.progress ?? 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: KLAYO_COLORS.GreyGR100,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: '#4CAF50',
                    },
                  }}
                />
              </Stack>
            ),
          });

        if (c.type === CourseStudentsTable.columns.progress)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.progress.field,
            headerName: translate?.(CourseStudentsTable.columns.progress.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 160,
            valueGetter: (_value, row) => row.progress,
            renderCell: (params) => (
              <Stack direction='row' alignItems='center' gap={1} sx={{ width: '100%' }}>
                <LinearProgress
                  variant='determinate'
                  value={params.row.progress ?? 0}
                  sx={{
                    width: 80,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: KLAYO_COLORS.GreyGR100,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: '#4CAF50',
                    },
                  }}
                />
                <Typography variant='body2'>{params.row.progress ?? 0}%</Typography>
              </Stack>
            ),
          });

        if (c.type === CourseStudentsTable.columns.dueDate)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.dueDate.field,
            headerName: translate?.(CourseStudentsTable.columns.dueDate.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 130,
            valueGetter: (_value, row) => row.dueDate,
            renderCell: (params) => {
              if (!params.row.dueDate)
                return (
                  <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                    {translate?.('manage.courseStudents.notSet')}
                  </Typography>
                );
              // Server-computed overdue (past due AND not passed), the same source of truth as the
              // Status column and the Overdue card — a completed-late row must not turn red here.
              const isOverdue = !!params.row.isOverdue;
              return (
                <Typography
                  variant='body2'
                  fontWeight={isOverdue ? 500 : 400}
                  color={isOverdue ? KLAYO_COLORS.TouchRedTR500 : KLAYO_COLORS.GreyGR900}
                >
                  {Utils.formatDate(params.row.dueDate)}
                </Typography>
              );
            },
          });

        if (c.type === CourseStudentsTable.columns.courseExpiry)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.courseExpiry.field,
            headerName: translate?.(c.label || CourseStudentsTable.columns.courseExpiry.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 140,
            valueGetter: (_value, row) => row.courseAccessExpiresAt,
            renderCell: (params) =>
              params.row.courseAccessExpiresAt ? (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
                  {Utils.formatDate(params.row.courseAccessExpiresAt)}
                </Typography>
              ) : (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                  {translate?.('manage.courseStudents.notSet')}
                </Typography>
              ),
          });

        if (c.type === CourseStudentsTable.columns.completedDate)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.completedDate.field,
            headerName: translate?.(CourseStudentsTable.columns.completedDate.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 150,
            valueGetter: (_value, row) => row.completedDate,
            renderCell: (params) => {
              return (
                <Typography variant='body2' fontWeight={400} color={KLAYO_COLORS.GreyGR900}>
                  {params.row.completedDate ? Utils.formatDate(params.row.completedDate) : ''}
                </Typography>
              );
            },
          });
        if (c.type === CourseStudentsTable.columns.enrolmentDate)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.enrolmentDate.field,
            headerName: translate?.(CourseStudentsTable.columns.enrolmentDate.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 150,
            valueGetter: (_value, row) =>
              row.enrolledDate ? Utils.formatDate(row.enrolledDate) : '',
          });

        if (c.type === CourseStudentsTable.columns.enrolledCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.enrolledCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.enrolledCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.enrolledCount,
            renderCell: (params) =>
              (params.row.enrolledCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.enrolledCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'enrolled')
                  }
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.completedCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.completedCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.completedCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.completedCount,
            renderCell: (params) =>
              (params.row.completedCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.completedCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'completed')
                  }
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.inProgressCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.inProgressCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.inProgressCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.inProgressCount,
            renderCell: (params) =>
              (params.row.inProgressCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.inProgressCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'inProgress')
                  }
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.notStartedCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.notStartedCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.notStartedCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.notStartedCount,
            renderCell: (params) =>
              (params.row.notStartedCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.notStartedCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'notStarted')
                  }
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.failedCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.failedCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.failedCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 100,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.failedCount,
            renderCell: (params) =>
              (params.row.failedCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.failedCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'failed')
                  }
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.overdueCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.overdueCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.overdueCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.overdueCount,
            renderCell: (params) =>
              (params.row.overdueCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.overdueCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'overdue')
                  }
                  textColor={KLAYO_COLORS.Red600}
                  backgroundColor={KLAYO_COLORS.Rose050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.pendingCount)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.pendingCount.field,
            headerName: this.props.translate?.(CourseStudentsTable.columns.pendingCount.label),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 110,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (_value, row) => row.pendingCount,
            renderCell: (params) =>
              (params.row.pendingCount ?? 0) > 0 ? (
                <Chip
                  label={params.row.pendingCount}
                  onClick={(e: SyntheticEvent) =>
                    onCountBadgeClick?.(e as any, params.row, 'pending')
                  }
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
          });

        if (c.type === CourseStudentsTable.columns.actions)
          this.columns.push({
            field: c.field || CourseStudentsTable.columns.actions.field,
            type: 'actions',
            filterable: false,
            align: 'right',
            width: 50,
            disableReorder: true,
            renderCell: (params) => [
              <GridActionsCellItem
                key='action'
                icon={<MoreVertIcon />}
                label='Actions'
                onClick={(e: any) => onStudentAction?.(e, params.row)}
              />,
            ],
          });
      }
    });
  }
}
